import type { DeliveryWindow } from '@croissants/contracts';
import { addServiceDays } from '@croissants/contracts/projections';
import {
  addressLine,
  formatDate,
  formatMoney,
  formatTime,
  formatWindow,
  type Locale,
  toServiceDay,
} from '@croissants/i18n';

/**
 * Formatting, always in the property's own timezone.
 *
 * The platform stores UTC instants and renders them where the delivery happens,
 * never where the reader happens to be (tech-stack section 8) - a guest checking
 * their order from a plane must still read the Paris morning. Every window
 * carries its IANA zone, so these helpers never have to assume one.
 *
 * Money crosses this boundary as integer minor units and is divided exactly once,
 * inside `formatMoney` (PRD NFR-003).
 */

export function money(minorUnits: number, currency: string, locale: Locale): string {
  return formatMoney(minorUnits, currency, locale);
}

/** "09:15-09:45" in the property's zone - the promised window (PRD GST-002). */
export function windowRange(window: DeliveryWindow, locale: Locale): string {
  return formatWindow(window.start, window.end, locale, window.timeZone);
}

/** "Monday 7 August" in the property's zone. */
export function dayLabel(
  instant: string,
  locale: Locale,
  timeZone: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  return options === undefined
    ? formatDate(instant, locale, timeZone)
    : formatDate(instant, locale, timeZone, options);
}

/** A service day (YYYY-MM-DD) rendered as a weekday and date in that zone. */
export function serviceDayLabel(serviceDay: string, locale: Locale, timeZone: string): string {
  // Midday UTC keeps the calendar date stable in every zone the platform serves,
  // so the label never slips a day for a guest in a westward timezone.
  return formatDate(`${serviceDay}T12:00:00.000Z`, locale, timeZone);
}

export function clockTime(instant: string, locale: Locale, timeZone: string): string {
  return formatTime(instant, locale, timeZone);
}

/**
 * Which of the strip's first two mornings this is, if either (PRD GST-002).
 *
 * `now` is the PLATFORM's clock - the picker anchors it on the answer's own
 * `serverTime` (TST-003) - never the device's, because a phone an hour out
 * would label a morning "Today" that the platform had already moved past.
 *
 * The server also tags today itself (`kind: 'TODAY'`), and the two cannot
 * disagree: both read one instant in the property's zone. It is asked here
 * rather than there because `kind` names today ONLY while immediate delivery
 * is on (RTE-034), and with it off the strip still opens on tomorrow and that
 * card is still "Tomorrow". One rule for both words, from one clock.
 */
export function relativeServiceDay(
  serviceDay: string,
  timeZone: string,
  now: Date | number,
): 'TODAY' | 'TOMORROW' | null {
  const today = toServiceDay(now, timeZone);
  if (serviceDay === today) return 'TODAY';
  return serviceDay === addServiceDays(today, 1) ? 'TOMORROW' : null;
}

/**
 * Whole days from the platform's today to a service morning (PRD GST-034).
 *
 * 0 is this morning, 1 tomorrow, 2 the morning after; a morning already past
 * comes back negative. Counted in DAYS on the property's calendar rather than
 * in elapsed hours, because that is the question being asked: an order for
 * 07:00 tomorrow is "tomorrow" whether it is now 21:00 tonight (ten hours) or
 * 05:00 this morning (twenty-six).
 *
 * `now` is the PLATFORM's clock, for the reason `relativeServiceDay` states
 * above it - the two share that rule and this one is the general case of it.
 * Both walk the calendar through `toServiceDay`, so a stay that crosses a
 * daylight-saving change counts the days a guest would count.
 */
export function serviceDaysAway(serviceDay: string, timeZone: string, now: Date | number): number {
  const today = toServiceDay(now, timeZone);
  // Midday UTC on both ends: no offset the platform serves can push a midday
  // instant onto a neighbouring date, so the difference is a whole number of
  // days on every zone and either side of a DST change.
  const from = Date.parse(`${today}T12:00:00.000Z`);
  const to = Date.parse(`${serviceDay}T12:00:00.000Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;
  return Math.round((to - from) / 86_400_000);
}

/** "Wed" / "mer." - the abbreviated weekday, per the locale's own convention. */
export function weekdayShort(serviceDay: string, locale: Locale, timeZone: string): string {
  return formatDate(`${serviceDay}T12:00:00.000Z`, locale, timeZone, { weekday: 'short' });
}

/**
 * The two lines under the weekday on a day card: "23" and "Aug".
 *
 * Two functions and not one formatted "23 Aug", because the card draws them at
 * two different sizes - the number is what a thumb aims at and reads at a
 * glance, the month is context and stays small. A single formatted string
 * cannot be split back into its parts in a locale-independent way (the French
 * card is "23 / aout" and the American one could be "Aug / 23"), so each part is
 * asked for on its own terms and stacked by the card.
 */
export function dayOfMonth(serviceDay: string, locale: Locale, timeZone: string): string {
  return formatDate(`${serviceDay}T12:00:00.000Z`, locale, timeZone, { day: 'numeric' });
}

/**
 * A postal address on one line: "12 rue de Bretagne, Apt 4B, 75003 Paris".
 *
 * Two screens print it and they must not disagree - the checkout's address
 * row, and the frame's "Delivering to" banner - and neither may disagree with
 * the other portals either: the join is `addressLine` in @croissants/i18n
 * (F9), the one every surface prints an address through.
 */
export const postalAddressLine = addressLine;

/**
 * A moment stamped on an order, relative to the morning that order is for
 * (PRD GST-036).
 *
 * One order spans two calendar days far more often than not: "each morning is its
 * own order, charged the night before and cancellable on its own", so an order is
 * placed at 21:40 and starts being prepared at 06:52. Stamped as bare clock times
 * those two lines read as a timeline running backwards, which is the opposite of
 * what GST-036 promises - stages only advance.
 *
 * The delivery morning is the reference the whole screen is written against: the
 * confirmed stage says "arriving {day}, {window}" and the ETA card repeats it. So
 * a stamp that falls on that morning stays a bare time, and any stamp that does
 * not carries its own date - the night-before ones, and a delivery that slipped
 * past midnight. Nothing about the joining is hardcoded: `Intl` orders the date,
 * the separator and the time per locale, day-first in both since the tag English
 * writes dates with lives in `formatDate` ("7 Aug, 21:40", "7 aout, 21:40").
 *
 * This header sat above `postalAddressLine` instead, two functions down, from
 * the day that function was inserted between the comment and what it describes.
 */
export function stampTime(
  instant: string,
  serviceDay: string,
  locale: Locale,
  timeZone: string,
): string {
  return toServiceDay(instant, timeZone) === serviceDay
    ? formatTime(instant, locale, timeZone)
    : formatDate(instant, locale, timeZone, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      });
}
